import React, { useEffect, useMemo, useCallback, Fragment } from 'react'
import PropTypes from 'prop-types'

// Constants
import { EVENT_TICKET_COMMENT_CREATED } from 'constants/events'
import { API_TICKET_COMMENT_LIST } from 'constants/endpoints'
import { EVENT_CLOSE_ALL_DROPDOWN } from 'constants/events'

// Api
import { tagTicketAsResolved } from 'api/ticket'

// Service
import event from 'services/event'
import dialog from 'services/dialog'

// Use
import useTable from 'use/table'
import useRouteChangeConfirm from 'use/route-change-confirm'

// Models
import TicketComment from 'models/TicketComment'

// Components
import Text from 'ui/Text'
import Loading from 'ui/Loading'
import Pagination from 'ui/Pagination/Pagination'
import TicketCommentItem from './TicketCommentItem'
import TicketCommentTagResolved from './TicketCommentTagResolved'

export const TicketCommentList = ({
  ticketId,
  showResolveConfirm,
  isCommentFormVisible,
  onCancel,
  onTicketClosed,
}) => {
  // Table
  const filters = useMemo(() => ({ ticketId }), [ticketId])
  const { loading, data, total, page, limit, onPageChange, getTableData } =
    useTable({
      url: API_TICKET_COMMENT_LIST,
      method: 'GET',
      filters,
      model: TicketComment,
    })

  const firstAdminCommentIndex = useMemo(
    () => data.findIndex((comment) => comment.isAdmin),
    [data]
  )

  // Handle
  const onResolve = useCallback(
    async ({ setConfirmLoading, setConfirmVisible, next }) => {
      try {
        setConfirmLoading(true)

        // Call api resolved
        await tagTicketAsResolved(ticketId)

        // Close confirm
        setConfirmVisible(false)

        // Emit event
        onTicketClosed()

        // Show alert
        dialog.alert({
          title: 'Ticket tagged as resolved!',
          message:
            'We’re happy to have helped you. Feel free to send us a message should you have any other questions or concerns.',
          onConfirm: ({ setVisible }) => {
            setVisible(false)

            if (next) {
              next()
            }
          },
        })
      } catch (error) {
        //
      } finally {
        setConfirmLoading(false)
      }
    },
    [ticketId, onTicketClosed]
  )

  // Resolve click
  const onResolveClick = useCallback(
    ({ next }) => {
      event.emit(EVENT_CLOSE_ALL_DROPDOWN)
      dialog.confirm({
        title: 'Were we able to resolve your ticket?',
        message:
          'Please confirm if we have answered/helped you with the message you sent us',
        confirmText: 'Yes, tag as resolved',
        cancelText: 'Not yet, send new comment',
        size: 'mdx',
        onConfirm: async ({ setLoading, setVisible }) => {
          await onResolve({
            next,
            setConfirmLoading: setLoading,
            setConfirmVisible: setVisible,
          })
        },
        onCancel: ({ setVisible }) => {
          setVisible(false)

          onCancel()
        },
      })
    },
    [onCancel, onResolve]
  )

  const isDisplayNoDataMessage = useMemo(() => {
    if (loading) {
      return false
    }

    if (data.length > 0) {
      return false
    }

    return isCommentFormVisible === false
  }, [data.length, isCommentFormVisible, loading])

  useRouteChangeConfirm(showResolveConfirm, onResolveClick)

  useEffect(() => {
    event.on(EVENT_TICKET_COMMENT_CREATED, getTableData)
    return () => {
      event.off(EVENT_TICKET_COMMENT_CREATED, getTableData)
    }
  })

  return (
    <div style={{ padding: '10px 0' }}>
      <Loading spinning={loading}>
        {data.map((comment, index) => (
          <Fragment key={index}>
            <TicketCommentItem comment={comment} />
            {index === firstAdminCommentIndex && showResolveConfirm && (
              <TicketCommentTagResolved
                onResolve={onResolveClick}
                onCancel={onCancel}
              />
            )}
          </Fragment>
        ))}

        {isDisplayNoDataMessage && <Text>No comments yet.</Text>}

        {total / limit > 1 && (
          <Pagination
            page={page}
            total={total}
            perPage={limit}
            data={data}
            onPageChange={onPageChange}
          />
        )}
      </Loading>
    </div>
  )
}

TicketCommentList.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showResolveConfirm: PropTypes.bool,
  isCommentFormVisible: PropTypes.bool,
  onCancel: PropTypes.func,
  onTicketClosed: PropTypes.func,
}

TicketCommentList.defaultProps = {
  ticketId: '',
  showResolveConfirm: false,
  isCommentFormVisible: false,
  onCancel: () => {},
  onTicketClosed: () => {},
}

export default TicketCommentList
